/**
 * @engine/ui — shared primitive types. Everything here is JSON-safe: parsed stylesheets and
 * compiled UI artifacts serialize these directly (the player never parses CSS).
 */

/** RGBA, 0..1 floats (UI is LDR — drawn after post-process). */
export type Color4 = readonly [number, number, number, number];

/**
 * Linear unit combination — the `calc()` normal form (also produced by bare em/vw/vh values):
 * px + pct·parent + vw/vh·(viewport LOGICAL %) + em·(effective font size). Resolved to plain
 * px / `{pct}` by UIDocument BEFORE the layout engine — yoga only ever sees px/%/auto.
 * Mixing `%` with absolute units inside one calc() is unsupported (warned, % part dropped):
 * the parent size isn't known pre-layout.
 */
export interface DimCalc {
  calc: { px?: number; pct?: number; vw?: number; vh?: number; em?: number };
}

/** A CSS dimension: number = px, `{ pct }` = percent of parent, 'auto', or a unit combo. */
export type Dim = number | { pct: number } | 'auto' | DimCalc;

export type FlexDirection = 'row' | 'row-reverse' | 'column' | 'column-reverse';
export type FlexWrap = 'nowrap' | 'wrap' | 'wrap-reverse';
export type JustifyContent =
  | 'flex-start'
  | 'center'
  | 'flex-end'
  | 'space-between'
  | 'space-around'
  | 'space-evenly';
export type AlignValue = 'auto' | 'flex-start' | 'center' | 'flex-end' | 'stretch' | 'baseline';
export type PositionValue = 'relative' | 'absolute';
export type DisplayValue = 'flex' | 'none';
export type OverflowValue = 'visible' | 'hidden' | 'scroll';
export type TextAlign = 'left' | 'center' | 'right';
export type PointerEventsValue = 'auto' | 'none';
export type WhiteSpaceValue = 'normal' | 'nowrap';
export type TextOverflowValue = 'clip' | 'ellipsis';

/** One `box-shadow` layer: `[inset] x y blur [spread] color`. `inset` draws inside the node. */
export interface ShadowDecl {
  x: number;
  y: number;
  blur: number;
  spread: number;
  color: Color4;
  /** inset shadows paint OVER the fills (inner shadow); outer ones paint under the node. */
  inset?: boolean;
}

/** `linear|radial|conic-gradient(…)` fill. `kind` defaults to linear. */
export interface GradientDecl {
  /** 'linear' (default) | 'radial' | 'conic'. */
  kind?: 'linear' | 'radial' | 'conic';
  /** linear: line angle; conic: start angle. CSS convention (0deg = to top, 90deg = to right). */
  angleDeg: number;
  /** radial/conic center as a fraction 0..1 of the box (default 0.5, 0.5). */
  cx?: number;
  cy?: number;
  /** radial extent: 'ellipse' (default, fits the box) | 'circle' (max side). */
  shape?: 'circle' | 'ellipse';
  /** 2..6 stops, pos 0..1 ascending (parser normalizes/distributes). */
  stops: readonly { color: Color4; pos: number }[];
}

/**
 * One `transform` function. Ordered list (`transform: A B C` applies A∘B∘C to a point). Translate
 * carries px AND fraction-of-own-size (%) per axis; rotate/skew are RADIANS (parser converts deg).
 * PAINT-ONLY — transforms never touch layout (layout stays 2D flexbox).
 *
 * UI-17c adds a 3D subset: `perspective(d)` (viewing distance px; must precede the 3D rotations it
 * projects), `rotateX`/`rotateY` (tilt out of the screen plane), `rotateZ` (== `rotate`), and
 * `translateZ` (depth). A flat z=0 box under these collapses to a 2D homography (see render/mat2d.ts),
 * so the flat CSS model holds — no `preserve-3d` scene graph (v1).
 */
export type TransformFn =
  | { fn: 'translate'; x: number; y: number; xPct: number; yPct: number }
  | { fn: 'scale'; x: number; y: number }
  | { fn: 'rotate'; rad: number }
  | { fn: 'skew'; x: number; y: number }
  | { fn: 'rotateX'; rad: number }
  | { fn: 'rotateY'; rad: number }
  | { fn: 'translateZ'; z: number }
  | { fn: 'perspective'; d: number };

export type TransformDecl = readonly TransformFn[];

/** `transform-origin` as fractions 0..1 of the border box (default center 0.5, 0.5). */
export interface TransformOrigin {
  x: number;
  y: number;
}

/** Named easing curves (cubic-bezier presets + linear) — see style/interpolate.ts. */
export type EasingName = 'linear' | 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out';

/** One `transition` entry: animate `prop` (a StyleDecls key or 'all') over a duration. */
export interface TransitionDecl {
  /** StyleDecls key to watch, or 'all'. */
  prop: string;
  durationMs: number;
  delayMs: number;
  easing: EasingName;
}

/** One `@keyframes` stop: a normalized offset (0..1; `from`=0, `to`=1) and its declarations. */
export interface KeyframeStop {
  pos: number;
  decls: StyleDecls;
}

/**
 * A parsed `@keyframes name { … }` track (UI-18b). Stops are ASCENDING by pos; 0/1 aren't
 * guaranteed present (the node's base value fills a missing endpoint). `props` = union of the
 * animated keys across all stops, precomputed so the sampler doesn't rescan every frame.
 */
export interface Keyframes {
  name: string;
  stops: readonly KeyframeStop[];
  props: readonly (keyof StyleDecls)[];
}

export type AnimationDirection = 'normal' | 'reverse' | 'alternate' | 'alternate-reverse';
export type AnimationFillMode = 'none' | 'forwards' | 'backwards' | 'both';

/**
 * One `animation` shorthand entry (UI-18b). Infinite play is a separate flag rather than
 * `iterations: Infinity` — the compiled stylesheet is JSON, and `JSON.stringify(Infinity)` is
 * `null`, which would silently break infinite loops in the player.
 */
export interface AnimationDecl {
  /** `@keyframes` name to play. */
  name: string;
  durationMs: number;
  delayMs: number;
  /** Segment easing applied between consecutive keyframes. */
  easing: EasingName;
  /** Finite iteration count (ignored when `infinite`). */
  iterations: number;
  infinite: boolean;
  direction: AnimationDirection;
  fillMode: AnimationFillMode;
}

/** Pseudo-class interaction states a node can be in (drive conditional style rules). */
export type PseudoState = 'hover' | 'active' | 'focus' | 'disabled';

export const PSEUDO_STATES: readonly PseudoState[] = ['hover', 'active', 'focus', 'disabled'];

/**
 * The full set of supported style declarations (the v1 CSS subset, parsed + typed).
 * One flat bag: layout properties feed the layout engine, paint/text feed rendering.
 * All properties optional — absence means "not set at this level of the cascade".
 */
export interface StyleDecls {
  // -- flexbox
  display?: DisplayValue;
  flexDirection?: FlexDirection;
  flexWrap?: FlexWrap;
  flexGrow?: number;
  flexShrink?: number;
  flexBasis?: Dim;
  justifyContent?: JustifyContent;
  alignItems?: AlignValue;
  alignSelf?: AlignValue;
  alignContent?: AlignValue;
  rowGap?: number;
  columnGap?: number;

  // -- box
  width?: Dim;
  height?: Dim;
  minWidth?: Dim;
  minHeight?: Dim;
  maxWidth?: Dim;
  maxHeight?: Dim;
  marginTop?: Dim;
  marginRight?: Dim;
  marginBottom?: Dim;
  marginLeft?: Dim;
  paddingTop?: Dim;
  paddingRight?: Dim;
  paddingBottom?: Dim;
  paddingLeft?: Dim;
  position?: PositionValue;
  top?: Dim;
  right?: Dim;
  bottom?: Dim;
  left?: Dim;
  aspectRatio?: number;
  overflow?: OverflowValue;
  // NOTE: borderWidth is listed under paint below but ALSO participates in layout
  // (CSS box model — yoga setBorder); it is included in LAYOUT_KEYS.

  // -- paint
  // NOTE: `null` on the nullable paint props = explicit `none` in the cascade — a JSON-safe
  // "unset what earlier rules set" sentinel (undefined would vanish in the compiled styles).
  backgroundColor?: Color4;
  /** Asset guid (resolved from `asset('…')` at import time). */
  backgroundImage?: string | null;
  /** Gradient fill — draws INSTEAD of the color fill (a material wins over both). */
  gradient?: GradientDecl | null;
  /** Box-shadow layers (CSS list order = topmost first); outer paint under the node, inset over the fills. */
  shadow?: readonly ShadowDecl[] | null;
  /** 9-slice insets in px: [top, right, bottom, left] (`-ui-slice`). */
  slice?: readonly [number, number, number, number];
  tint?: Color4;
  opacity?: number;
  borderRadius?: number;
  borderWidth?: number;
  borderColor?: Color4;
  /**
   * Backdrop blur radius in logical px (`-ui-backdrop-blur`) — acrylic base. Draws the blurred
   * backbuffer under the node BEFORE its background; a translucent backgroundColor is the tint.
   */
  backdropBlur?: number;
  /** 0..1 acrylic grain amount (`-ui-backdrop-noise`); only visible with backdropBlur > 0. */
  backdropNoise?: number;
  /**
   * UI material asset guid (`-ui-material: asset('….slang')`, resolved at import like
   * backgroundImage). Replaces the background rect: the material shades the rounded rect,
   * receiving backgroundColor as its tint input. Border/text still paint on top.
   */
  material?: string;
  /**
   * Hit-test participation (input, not paint). Resolved via nearest-ancestor lookup:
   * `none` makes the subtree click-through, a child's `auto` re-enables it AND force-opts
   * the node in as a hit target (transparent containers are click-through by default).
   */
  pointerEvents?: PointerEventsValue;
  /**
   * 2.5D transform (UI-17), PAINT-ONLY: the paint walk bakes it (+ `transformOrigin`) into a matrix
   * and transforms the quad corners; the subtree inherits it; hit-test inverse-maps the pointer.
   * Layout is unaffected. `null` = explicit `none` (JSON-safe unset sentinel in the cascade).
   */
  transform?: TransformDecl | null;
  transformOrigin?: TransformOrigin;

  // -- animation
  /** `transition` list (UI-18): watched props tween toward their resolved target over the doc clock. */
  transition?: readonly TransitionDecl[] | null;
  /** `animation` list (UI-18b): named `@keyframes` tracks played over the doc clock; override the base. */
  animation?: readonly AnimationDecl[] | null;

  // -- text (inherited)
  color?: Color4;
  /** Font asset guid — an explicit face override; wins over `fontFamily`. */
  font?: string;
  /** Font family stack (names, comma list); resolved to a face via `fontWeight`/`fontStyle`, falling
   *  through the stack for the glyph-fallback chain (W-TEXT). */
  fontFamily?: readonly string[] | null;
  /** 100..900 (normal = 400, bold = 700). */
  fontWeight?: number;
  fontStyle?: FontStyleValue;
  fontSize?: number;
  /** Multiplier of fontSize. */
  lineHeight?: number;
  textAlign?: TextAlign;
  letterSpacing?: number;
  whiteSpace?: WhiteSpaceValue;
  /** NOT inherited (CSS parity): set it on the <text> element itself. */
  textOverflow?: TextOverflowValue;
}

/** `font-style` (oblique folds to italic for face matching in v1). */
export type FontStyleValue = 'normal' | 'italic';

/** Style properties that INHERIT down the tree (CSS-like). */
export const INHERITED_KEYS = [
  'color',
  'font',
  'fontFamily',
  'fontWeight',
  'fontStyle',
  'fontSize',
  'lineHeight',
  'textAlign',
  'letterSpacing',
  'whiteSpace',
] as const satisfies readonly (keyof StyleDecls)[];

/** Style properties that affect LAYOUT (a change marks the layout node dirty). */
export const LAYOUT_KEYS = [
  'display',
  'flexDirection',
  'flexWrap',
  'flexGrow',
  'flexShrink',
  'flexBasis',
  'justifyContent',
  'alignItems',
  'alignSelf',
  'alignContent',
  'rowGap',
  'columnGap',
  'width',
  'height',
  'minWidth',
  'minHeight',
  'maxWidth',
  'maxHeight',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'position',
  'top',
  'right',
  'bottom',
  'left',
  'aspectRatio',
  'overflow',
  'borderWidth',
] as const satisfies readonly (keyof StyleDecls)[];

/** Text properties that affect measurement (a change re-measures the text node). */
export const TEXT_MEASURE_KEYS = [
  'font',
  'fontFamily',
  'fontWeight',
  'fontStyle',
  'fontSize',
  'lineHeight',
  'letterSpacing',
  'textAlign',
  'whiteSpace',
] as const satisfies readonly (keyof StyleDecls)[];

/** One selector compound: `.a.b:hover` → classes ['a','b'], pseudo ['hover']. */
export interface SelectorCompound {
  classes: string[];
  pseudo: PseudoState[];
}

/**
 * One parsed rule. `classes`/`pseudo` describe the SUBJECT (last) compound; `ancestors` (when
 * present) are the descendant-combinator compounds, OUTERMOST FIRST (`.a .b .c` → ancestors
 * [.a, .b], subject .c) — each must match some ancestor above the previous match.
 */
export interface StyleRule {
  classes: string[];
  pseudo: PseudoState[];
  ancestors?: SelectorCompound[];
  decls: StyleDecls;
}

/** A parsed stylesheet (the import-time product of one SFC `<style>` block). */
export interface StyleSheetData {
  rules: StyleRule[];
  /** `@keyframes` tracks by name (UI-18b). Omitted when the sheet declares none. */
  keyframes?: Record<string, Keyframes>;
}

export interface UIDiagnostic {
  severity: 'error' | 'warning';
  message: string;
  line: number;
  col: number;
}
